import React, { JSX } from "react";

import { InstantString } from "api/model/general/aliases";
import { OverriddenColumnValues } from "components/ComponentTypeTypes";
import {
  DashboardComponentPropsBase,
  IComponentTimed,
} from "components/pages/dashboard/components/interfaces";
import {
  CellSubType,
  CellType,
  ColumnAggregationType,
  ProductTemplateSortingStrategy,
  ProductTemplateSortingStrategyOptionsOrder,
  SignStatus,
  TemplateLoadingType,
} from "utils/enums";
import { ChronoUnitLegacy, TimeSpan } from "utils/time-utils";
import { IForeignKeyValue } from "../components/templates/foreign-key/ForeignKeyTypes";

export type Nullable<T> = T | null;

export type ProductTemplateTableTransposedProps = {
  configData: IProductTemplate | undefined;
  templateData: Array<ITableDataEntry>;

  selectedEntryIndex: number | undefined;
  selectedEntryId: string | undefined;
  selectedEntryBody: ITableDataEntry | undefined;
  page: number;
  setPage: (page: number) => void;

  reportOptions?: ReportOptions;
  itemsPerPage?: number;
  foreignKeyWrap?: boolean;
  showTotals?: boolean;
  hidePagination?: boolean;

  handleTableKeyDown(key: string): void;
  EditRowDataCell(val: ITableDataEntry, index: number): JSX.Element;
  editedDataHandler(
    indexEntry: number,
    refName: string | undefined,
    newValue: any,
    newCell?: ITableDataEntryValue
  ): void;
  onClickSign(entryIndex: number, referenceName: string): void;
};

export interface IProductTemplateTableProps
  extends DashboardComponentPropsBase<ReportOptions>,
    IDateRange,
    IComponentTimed,
    ProductTemplate.IHorizontalCommonConfig {
  safeName: string;
  transposed?: boolean;
  showTitle?: boolean;
  showTotals?: boolean;
  updateFrequency?: number;
  hiddenColumns?: Array<string>;
  onlyShownColumns?: Array<string>;
  itemsPerPage?: number;
  overrideLoadingConfig?: LoadingConfig;
  overriddenColumnValues?: OverriddenColumnValues;
  filter?: string | null;
  defaultFilter?: string;
  onFetchError?: (val: string) => void;
  exactDateFrom?: Date;
  exactDateTo?: Date;
  hideDates?: boolean;
  showFilter?: boolean;
  useExternalFilterEndpoint?: boolean;
  allowAddEntry?: boolean;
  allowRowEdit?: boolean;
  foreignKeyWrap?: boolean;
  hideHeader?: boolean;
  hidePagination?: boolean;
  isColumnsSortable?: boolean;
  onFilterChange?: (filter: string) => void;
}

// Table config interface
export interface IProductTemplate {
  id?: number;
  displayName: string;
  safeName: string;
  keyColumn: string;
  metaKeyColumn: string;
  data?: IProductTemplateData;
  dateCreated?: InstantString;
  dateModified?: InstantString;
  company?: string;
}

export interface IProductTemplateData {
  attributes: IProductTemplateAttribute[];
  config?: IProductTemplateConfig;
  parameterTemplates: IParameterSectionTemplate[];
}

export interface IProductTemplateAttribute {
  // TODO choose required
  referenceName: string;
  displayName: string;
  description?: string;
  type: CellType;
  subType?: CellSubType;
  unit?: string;
  shortenText?: boolean;
  decimalPlaces?: number;
  defaultBgColor?: string;
  defaultValue?: string | number;
  autoFillTime?: boolean;
  selectionValues?: Nullable<(TimeSpan | string)[]>;
  locked?: boolean;
  editRequireRoles?: string[];
  aggregationType?: ColumnAggregationType;
  evaluationExpression?: string;
  dateDisplayPattern?: string;
  foreignTemplate?: string;
  bgHighlightRanges?: Nullable<HighlightRange[]>;
  highlightColor?: Nullable<HighlightColor>;
  validationOptions?: ValidationOptions;
}

export interface IProductTemplateConfig {
  toStringFormatter?: string;
  startAttribute?: string;
  stopAttribute?: string;
  signatureAttribute?: string;
  rowsPerPage?: number;
  relations?: ITemplateRelation[];
  loadingType?: TemplateLoadingType;
  loadingConfig?: IPeriodLoadingConfig;
  sortingStrategy?: ProductTemplateSortingStrategy;
  sortingStrategyOptions?: ProductTemplateSortingStrategyOptions;
  sortingReversed?: boolean;
  displayTotalRow?: boolean;
  allowGenerateReport?: boolean;
  allowRowAddition?: boolean;
  allowRowDeletion?: boolean;
  allowRowEdit?: boolean;
  timezoneId?: string;
}

export interface SignProductTemplateEntryResponse {
  display: string;
  id: number;
  signStatus: SignStatus;
  signed: boolean;
  signedAt?: string;
  validationErrors: ValidationError[];
}

export interface ValidationError {
  attribute: string;
  attributeDisplayName: string;
  message?: string;
  type: ValidationType;
}

interface IParameterSectionTemplate {
  sectionTitle: string;
  parameters: any[];
}

export interface LoadingConfig {
  loadingType?: TemplateLoadingType;
  loadingConfig?: IPeriodLoadingConfig;
}

interface ITemplateRelation {
  templateReference?: string;
  joinAttributes?: Map<string, string>;
}

export interface IPeriodLoadingConfig {
  defaultConfigUnit?: ChronoUnitLegacy;
  truncateStartUnit?: ChronoUnitLegacy;
  periodUnit?: ChronoUnitLegacy;
  period?: number;
  offsetUnit?: ChronoUnitLegacy;
  offset?: number;
}

export interface ProductTemplateSortingStrategyOptions {
  columns?: ProductTemplateColumnSortOptions[];
}

export interface ProductTemplateColumnSortOptions {
  column: string;
  order?: Nullable<ProductTemplateSortingStrategyOptionsOrder>;
}

// Table data interface
export interface ITableDataEntry {
  id?: string;
  template: string;
  entries: Record<string, ITableDataEntryValue>;
  dateCreated: string;
  dateModified: string;
  keyValue: any;
  stringRepresentation?: string;
}

export interface ITableDataEntryId {
  timestamp?: number;
  counter?: number;
  randomValue1?: number;
  randomValue2?: number;
}

export interface ReportOptions {
  splitAfter?: number;
  fontSize?: string;
}

export interface ITableDataEntryValue {
  referenceName: string;
  type: string;
  value: number | string | IUserSignature | ITimespan | IForeignKeyValue | MultiValue;
}

export interface ITableDataEntryModified {
  templateRef: string;
  entryData: ITableDataEntryValue[];
}

export interface IDateRange {
  from?: string;
  to?: string;
}

export interface HighlightRangeValue {
  value: number;
  inclusive: boolean;
}

export interface HighlightRange {
  min?: HighlightRangeValue;
  max?: HighlightRangeValue;
  color: string;
  bgColor: string;
}

export interface HighlightColor {
  color?: string;
  bgColor?: string;
}

export interface ValidationOptions {
  requiredAttributes?: string[];
  validators?: Validation[];
}

export interface Validation {
  message?: string;
  value?: string;
  type: ValidationType;
}

export type ValidationTypeInfo = {
  short: string;
  long: string;
};

export const commonValidationTypes = [
  { short: "EQ", long: "Equal to" },
  { short: "NE", long: "Not equal to" },
  { short: "REGEX", long: "Match regex" },
  { short: "MIN_LENGTH", long: "Min length" },
  { short: "MAX_LENGTH", long: "Max length" },
  { short: "CONTAINS", long: "Contains" },
  { short: "STARTS_WITH", long: "Starts with" },
  { short: "ENDS_WITH", long: "Ends with" },
  { short: "NOT_EMPTY", long: "Not empty" },
] as const;

export const validationTypes = [
  { short: "GT", long: "Greater than" },
  { short: "GTE", long: "Greater or equal to" },
  { short: "LT", long: "Less than" },
  { short: "LTE", long: "Less or equal to" },
  ...commonValidationTypes,
] as const;

export type ValidationType = (typeof validationTypes)[number]["short"];

export namespace ProductTemplate {
  export interface IHorizontalCommonConfig {
    editColumnLocation?: EditColumnLocation;
  }

  export type EditColumnLocation = "FRONT" | "BACK";
}

export interface ProductTemplateNonTransposedTableProps
  extends ProductTemplate.IHorizontalCommonConfig {
  configData: IProductTemplate | undefined;
  templateData: Array<ITableDataEntry>;

  selectedEntryIndex: number | undefined;
  selectedEntryId: string | undefined;
  selectedEntryBody: ITableDataEntry | undefined;
  page: number;
  setPage: (page: number) => void;
  allowRowEdit: boolean;
  foreignKeyWrap?: boolean;
  showTotals?: boolean;
  hidePagination?: boolean;
  isColumnsSortable?: boolean;
  setTemplateData: React.Dispatch<React.SetStateAction<Array<ITableDataEntry>>>;

  itemsPerPage?: number;

  reportOptions?: ReportOptions;

  EditRowDataCell(val: ITableDataEntry, index: number): JSX.Element;

  editedDataHandler(
    indexEntry: number,
    refName: string | undefined,
    newValue: any,
    newCell?: ITableDataEntryValue
  ): void;

  onClickSign(entryIndex: number, referenceName: string): void;

  handleTableKeyDown(key: string): void;
}

export interface IColumnsWithValuesProps {
  aggregationType?: ColumnAggregationType;
  referenceName: string;
  decimalPlaces?: number;
  values: Array<any>;
  total?: number;
  unit?: string;
}

export type Filter = {
  reference: string;
  validation: string;
  value: string;
};

export interface IAttributeHeaderProps {
  attribute: IProductTemplateAttribute;
  noTh?: boolean;
}

export interface IHeaderProps {
  displayName?: string;
  referenceName?: string;
  description?: string;
  noTh?: boolean;
}

interface ICellProps {
  isInEditMode: boolean;
  className?: string;
  changeHandler: (
    receivedValue?: any,
    refName?: string,
    newValue?: any,
    newCell?: ITableDataEntryValue | undefined
  ) => void;
  highlightColor?: Nullable<HighlightColor>;
}

export interface IEditDataCellProps {
  index: number;
  onClickEdit: any;
  onClickSave: any;
  onClickDelete: any;
  onClickCancel: any;
  onClickCopy: any;
  isInEditMode: boolean;
  deletable: boolean;
  signedBy: string;
}

export interface IDataCellProps extends ICellProps {
  headerConfig: IProductTemplateAttribute;
  dataValue: ITableDataEntry;
  indexHeader: number;
  indexEntry: number;
  onClickSign: any;
  foreignKeyWrap?: boolean;
  editRequireRoles: string[];
}

export interface MultiValue {
  subType: CellSubType;
  values: (TimeSpan | string | number)[];
}

export interface ILongProps extends ICellProps {
  value: number;
  unit: string;
  selectionValues: Nullable<(string | TimeSpan)[]> | undefined;
  formatNumber?: boolean;
  bgHighlightRanges?: Nullable<Array<HighlightRange>>;
}

export interface IDoubleProps extends ILongProps {
  precision: number;
}

export interface IDateTimeProps extends ICellProps {
  value: string;
  format: string;
}

export interface IInstantProps extends ICellProps {
  value: string;
  format: string;
}

export interface ITimeProps extends ICellProps {
  value: string;
  unit: string;
}

export interface IStringProps extends ICellProps {
  value: string;
  unit: string;
  selectionValues: Nullable<Array<string>>;
  shortenText: boolean;
}

export interface ITimespanProps extends ICellProps {
  timespan?: TimeSpan;
}

export interface ITimespan {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
}

export interface IUserSignature {
  id: number;
  display: string;
  signed: boolean;
  signedAt?: Nullable<string>;
}

export interface IUserSignatureProps extends ICellProps {
  value?: IUserSignature;
}
